import React, { useState, useEffect } from 'react';
import { 
  Users as UsersIcon, 
  ShieldCheck, 
  Edit2, 
  Eye, 
  Search, 
  Plus, 
  Trash2, 
  Save,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Lock,
  Unlock
} from 'lucide-react';
import { collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc, 
  serverTimestamp, 
  getDoc,
  getDocs,
  setDoc,
  deleteDoc } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { MODULES } from '../constants';
import { cn } from '../lib/utils';

interface UserAccessRightsViewProps {
  orgId: string;
}

interface UserPermission {
  id: string;
  fullName: string;
  email: string;
  role: string;
  permissions: string[];
  suspended: boolean;
}

export default function UserAccessRightsView({ orgId }: UserAccessRightsViewProps) {
  const [activeTab, setActiveTab] = useState<'users' | 'edit-rights' | 'view-rights'>('users');
  const [users, setUsers] = useState<UserPermission[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserPermission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizeLocalUser = (user: any, fallback?: UserPermission): UserPermission => ({
    id: String(user.id ?? user.membershipId ?? user.membership_id ?? user.userId ?? user.user_id ?? fallback?.id ?? ''),
    fullName: user.displayName ?? user.display_name ?? user.fullName ?? user.full_name ?? fallback?.fullName ?? 'Unknown Staff',
    email: user.email ?? fallback?.email ?? 'no-email',
    role: user.role ?? fallback?.role ?? 'staff',
    permissions: Array.isArray(user.permissions) ? user.permissions : fallback?.permissions ?? [],
    suspended: user.suspended ?? user.isSuspended ?? user.is_suspended ?? fallback?.suspended ?? false
  });

  const replaceLocalUser = (updatedUser: UserPermission) => {
    setUsers(current => current.map(user => user.id === updatedUser.id ? updatedUser : user));
    setSelectedUser(current => current?.id === updatedUser.id ? updatedUser : current);
  };

  useEffect(() => {
    if (!orgId) return;

    const appAuthToken = localStorage.getItem('app_auth_token');
    if (appAuthToken) {
      const controller = new AbortController();

      const loadLocalUsers = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/users`, {
            headers: { Authorization: `Bearer ${appAuthToken}` },
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`Failed to load organization users: ${response.status}`);

          const payload = await response.json();
          const rawUsers = Array.isArray(payload) ? payload : payload.users ?? payload.data ?? [];
          const orgUsers: UserPermission[] = (Array.isArray(rawUsers) ? rawUsers : []).map((user: any, index: number) =>
            normalizeLocalUser(user, { id: String(index), fullName: 'Unknown Staff', email: 'no-email', role: 'staff', permissions: [], suspended: false })
          );

          if (!controller.signal.aborted) {
            setUsers(orgUsers);
            setSelectedUser(current => current ? orgUsers.find(user => user.id === current.id) ?? current : null);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to load organization users', error);
            setUsers([]);
          }
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      };

      loadLocalUsers();
      return () => controller.abort();
    }

    const q = query(collection(db, 'org_users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
      const orgUsers = allUsers.filter((u: any) => 
        u.memberships?.some((m: any) => m.orgId === orgId)
      ).map((u: any) => {
        const membership = u.memberships?.find((m: any) => m.orgId === orgId);
        return {
          id: u.id,
          fullName: u.fullName || 'Unknown Staff',
          email: u.email || 'no-email',
          role: membership?.role || 'staff',
          permissions: membership?.permissions || [],
          suspended: membership?.suspended ?? false
        };
      });
      setUsers(orgUsers);
      setLoading(false);
      
      if (selectedUser) {
        const updated = orgUsers.find(u => u.id === selectedUser.id);
        if (updated) setSelectedUser(updated);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'org_users');
    });

    return () => unsubscribe();
  }, [orgId]);

  const handleTogglePermission = async (perm: string) => {
    if (!selectedUser || !orgId) {
      notify.error('Please select a user first');
      return;
    }

    const newPermissions = selectedUser.permissions.includes(perm)
      ? selectedUser.permissions.filter(p => p !== perm)
      : [...selectedUser.permissions, perm];

    setSaving(true);
    try {
      const appAuthToken = localStorage.getItem('app_auth_token');
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/users/${selectedUser.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: selectedUser.role,
            permissions: newPermissions,
            suspended: selectedUser.suspended
          })
        });
        if (!response.ok) throw new Error(`Failed to update organization user: ${response.status}`);

        const payload = await response.json();
        const rawUser = payload.user ?? payload.data ?? payload;
        replaceLocalUser(normalizeLocalUser(rawUser, { ...selectedUser, permissions: newPermissions }));
        notify.confirm(`Permission ${newPermissions.includes(perm) ? 'added' : 'removed'}`);
        return;
      }

      const userRef = doc(db, 'org_users', selectedUser.id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const memberships = userData.memberships || [];
        const updatedMemberships = memberships.map((m: any) => {
          if (m.orgId === orgId) {
            return { ...m, permissions: newPermissions };
          }
          return m;
        });

        await updateDoc(userRef, {
          memberships: updatedMemberships,
          updatedAt: serverTimestamp()
        });
        notify.confirm(`Permission ${newPermissions.includes(perm) ? 'added' : 'removed'}`);
      }
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, 'org_users');
      notify.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderModuleRights = (type: 'view' | 'edit') => {
    if (!selectedUser) {
      return (
        <div className="flex flex-col items-center justify-center p-12 bg-muted/20 rounded-3xl border border-dashed text-center">
          <UsersIcon className="w-12 h-12 text-muted-foreground mb-4 opacity-20" />
          <h3 className="text-lg font-bold">No User Selected</h3>
          <p className="text-sm text-muted-foreground">Select a user from the Users tab to manage their {type} rights.</p>
          <button 
            onClick={() => setActiveTab('users')}
            className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-xl font-bold text-xs uppercase tracking-widest hover:scale-105 active:scale-95 transition-all"
          >
            Go to Users
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between bg-white p-6 rounded-3xl border shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl shadow-inner">
              {(selectedUser.fullName || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-black text-lg tracking-tight uppercase">{selectedUser.fullName}</h3>
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">{selectedUser.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-xl border border-black/5">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span className="text-[10px] font-black uppercase tracking-widest text-primary">{selectedUser.role}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {MODULES.map((module) => {
            const isModulePermitted = selectedUser.permissions.includes(`${module.id}.${type}`);
            return (
              <div key={module.id} className="bg-white border rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all group">
                <div 
                  className={cn(
                    "p-6 flex items-center justify-between cursor-pointer transition-colors",
                    isModulePermitted ? "bg-primary/[0.02]" : "hover:bg-muted/30"
                  )}
                  onClick={() => handleTogglePermission(`${module.id}.${type}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-2xl shadow-sm transition-all group-hover:scale-110",
                      isModulePermitted ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}>
                      {module.icon}
                    </div>
                    <div>
                      <h4 className="font-black tracking-tight text-lg uppercase">{module.label}</h4>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                        {type === 'view' ? 'View' : 'Manage'} access for {module.label} module
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border transition-all",
                      isModulePermitted 
                        ? "bg-green-500/10 text-green-600 border-green-500/20" 
                        : "bg-muted text-muted-foreground border-black/5"
                    )}>
                      {isModulePermitted ? 'Enabled' : 'Disabled'}
                    </span>
                    <div className={cn(
                      "w-12 h-6 rounded-full relative transition-all duration-300",
                      isModulePermitted ? "bg-primary shadow-inner" : "bg-muted"
                    )}>
                      <div className={cn(
                        "absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 shadow-lg",
                        isModulePermitted ? "left-7" : "left-1"
                      )} />
                    </div>
                  </div>
                </div>

                {module.subModules.length > 0 && (
                  <div className="px-6 py-4 bg-muted/10 border-t grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {module.subModules.map((sub) => {
                      const isSubPermitted = selectedUser.permissions.includes(`${sub.id}.${type}`);
                      return (
                        <button
                          key={sub.id}
                          onClick={() => handleTogglePermission(`${sub.id}.${type}`)}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-xl border transition-all text-left group/sub",
                            isSubPermitted 
                              ? "bg-white border-primary/20 shadow-sm ring-1 ring-primary/5" 
                              : "bg-white/50 border-black/5 hover:border-black/10 text-muted-foreground"
                          )}
                        >
                          <span className={cn(
                            "text-[10px] font-bold uppercase tracking-widest",
                            isSubPermitted ? "text-primary" : ""
                          )}>{sub.label}</span>
                          {isSubPermitted ? (
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                          ) : (
                            <XCircle className="w-3 h-3 opacity-20 group-hover/sub:opacity-50 transition-opacity" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 animate-pulse">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm font-black uppercase tracking-[0.2em] text-muted-foreground">Loading Security Protocols...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Tab Navigation */}
      <div className="flex p-1.5 bg-muted/30 rounded-2xl border w-fit mx-auto shadow-sm backdrop-blur-sm">
        <button
          onClick={() => setActiveTab('users')}
          className={cn(
            "flex items-center gap-2 px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
            activeTab === 'users' ? "bg-white shadow-xl text-primary" : "text-muted-foreground hover:bg-white/50"
          )}
        >
          <UsersIcon className="w-4 h-4" />
          Users
        </button>
        <button
          onClick={() => setActiveTab('edit-rights')}
          className={cn(
            "flex items-center gap-2 px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
            activeTab === 'edit-rights' ? "bg-white shadow-xl text-primary" : "text-muted-foreground hover:bg-white/50"
          )}
        >
          <Edit2 className="w-4 h-4" />
          Edit Rights
        </button>
        <button
          onClick={() => setActiveTab('view-rights')}
          className={cn(
            "flex items-center gap-2 px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
            activeTab === 'view-rights' ? "bg-white shadow-xl text-primary" : "text-muted-foreground hover:bg-white/50"
          )}
        >
          <Eye className="w-4 h-4" />
          View Rights
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center justify-between bg-white p-6 rounded-3xl border shadow-sm">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search staff members..."
                className="w-full pl-12 pr-4 py-3 bg-muted/30 border-none rounded-xl outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold text-sm"
              />
            </div>
            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">
              Total Staff: <span className="text-primary">{filteredUsers.length}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <div 
                key={user.id} 
                onClick={() => setSelectedUser(user)}
                className={cn(
                  "p-6 bg-white border rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden",
                  selectedUser?.id === user.id ? "ring-2 ring-primary ring-offset-4" : ""
                )}
              >
                {selectedUser?.id === user.id && (
                  <div className="absolute top-0 right-0 p-4">
                    <CheckCircle2 className="w-6 h-6 text-primary animate-in zoom-in duration-300" />
                  </div>
                )}
                
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-muted group-hover:bg-primary transition-colors flex items-center justify-center text-xl font-black shadow-inner">
                    <span className="group-hover:text-primary-foreground transform group-hover:rotate-12 transition-all">
                      {(user.fullName || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-black text-lg tracking-tight uppercase line-clamp-1">{user.fullName}</h3>
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{user.role}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-3 bg-muted/30 rounded-xl">
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-1 leading-none">Email Address</p>
                    <p className="text-[11px] font-bold text-foreground/80 truncate leading-none">{user.email}</p>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 flex flex-col">
                      <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Permissions</span>
                      <span className="text-sm font-black text-primary">{user.permissions.length} Activated</span>
                    </div>
                    <ChevronRight className={cn(
                      "w-5 h-5 text-primary transition-all",
                      selectedUser?.id === user.id ? "translate-x-2" : "group-hover:translate-x-1"
                    )} />
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t flex items-center justify-between">
                   <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab('edit-rights');
                      setSelectedUser(user);
                    }}
                    className="p-2.5 bg-muted rounded-xl hover:bg-primary hover:text-primary-foreground transition-all group/btn"
                    title="Manage Permissions"
                  >
                    <ShieldCheck className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                  </button>
                  <div className="flex gap-2">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                      user.role === 'admin' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredUsers.length === 0 && (
              <div className="col-span-full py-20 text-center bg-muted/20 rounded-[2rem] border-2 border-dashed">
                <UsersIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-10" />
                <h3 className="text-xl font-black uppercase tracking-tighter">No users found</h3>
                <p className="text-sm text-muted-foreground">Try adjusting your search terms or add a new staff member.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {(activeTab === 'edit-rights' || activeTab === 'view-rights') && renderModuleRights(activeTab === 'edit-rights' ? 'edit' : 'view')}
    </div>
  );
}
